import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Alert,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Box,
} from '@mui/material';
import { ContextSession } from "../../services/ContextSession";

const CierreCaso = ({ 
  open, 
  demandaId, 
  onClose = () => {}, 
  onSuccess = () => {}, 
  setDemanda = () => {},
  estadoActual = '',
  avances = [],
  setAvances = () => {},
  setEstadoActual = () => {}
}) => {
  const [formData, setFormData] = useState({
    fechafinalizacion: '',
    resultadocierre: '',
    descripcioncierre: '',
    valordefinitivoconciliado: '',
    cuentavalordefinitivo: '',
    montogastoslegales: ''
  });
  const [demandaInfo, setDemandaInfo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [cargandoDatos, setCargandoDatos] = useState(false);
  const [error, setError] = useState(null);
  const [erroresCampos, setErroresCampos] = useState({});
  
  const sessi = useContext(ContextSession);
  
  // Opciones para resultado de cierre
  const opcionesResultado = [
    { value: 1, label: 'Favorable' },
    { value: 2, label: 'Desfavorable' },
    { value: 3, label: 'Conciliación' },
    { value: 4, label: 'Proceso desistido o abandono de proceso' },
    { value: 5, label: 'Otros' }
  ];

  // Obtener datos de la demanda cuando se abre el diálogo
  useEffect(() => {
    if (open && demandaId) {
      obtenerDatosDemanda();
      resetearFormulario();
    }
  }, [open, demandaId]);

  const obtenerDatosDemanda = async () => {
    try {
      setCargandoDatos(true);
      const encabezados = obtenerEncabezados();
      
      const response = await fetch(
        `https://bei360.injupemp.gob.hn:8444/api/tramite/${demandaId}/`,
        { headers: encabezados }
      );
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const datos = await response.json();
      setDemandaInfo(datos);
      
      // Si la API devuelve un array, tomar el primer elemento
      if (Array.isArray(datos) && datos.length > 0) {
        setDemandaInfo(datos[0]);
      } else {
        setDemandaInfo(datos);
      }
    } catch (error) {
      console.error('Error al obtener datos de la demanda:', error);
      setError(`Error al cargar datos: ${error.message}`);
    } finally {
      setCargandoDatos(false);
    }
  };

  const obtenerEncabezados = () => {
    const token = sessi?.sessionData?.token;
    if (!token) {
      throw new Error('No se encontró el token de autenticación');
    }
    
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  };

  const resetearFormulario = () => {
    setFormData({
      fechafinalizacion: '',
      resultadocierre: '',
      descripcioncierre: '',
      valordefinitivoconciliado: '',
      cuentavalordefinitivo: '',
      montogastoslegales: ''
    });
    setError(null);
    setErroresCampos({});
  };

  const manejarCierre = () => {
    resetearFormulario();
    onClose();
  };

  const manejarCambio = (campo) => (event) => {
    let valor = event.target.value;
    
    // Para campos numéricos, formatear correctamente
    if (campo === 'valordefinitivoconciliado' || campo === 'montogastoslegales') {
      // Permitir solo números y punto decimal
      valor = valor.replace(/[^0-9.]/g, '');
      
      // Validar que solo tenga un punto decimal
      const puntos = valor.split('.').length - 1;
      if (puntos > 1) {
        return; // No actualizar si hay más de un punto
      }
      
      // Si empieza con punto, agregar 0 antes
      if (valor.startsWith('.')) {
        valor = '0' + valor;
      }
      
      // Formatear decimales
      if (valor.includes('.')) {
        const partes = valor.split('.');
        if (partes[1].length > 2) {
          partes[1] = partes[1].substring(0, 2);
          valor = partes.join('.');
        }
      }
      
      // Validar longitud máxima de enteros (14 dígitos)
      const partes = valor.split('.');
      if (partes[0].length > 14) {
        partes[0] = partes[0].substring(0, 14);
        valor = partes.join('.');
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [campo]: valor
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (erroresCampos[campo]) {
      setErroresCampos(prev => ({
        ...prev,
        [campo]: ''
      }));
    }
    
    // Limpiar error general
    if (error) {
      setError(null);
    }
  };

  // Convertir fecha de formato YYYY-MM-DD a AAAAMMDD
  const convertirFechaAAAAMMDD = (fechaInput) => {
    if (!fechaInput) return '';
    
    const fecha = new Date(fechaInput);
    const anio = fecha.getFullYear();
    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
    const dia = String(fecha.getDate()).padStart(2, '0');
    
    return `${anio}${mes}${dia}`;
  };

  // ========== VALIDACIONES EXACTAS CON MENSAJES CLAROS ==========
  
  const validarFechaFinalizacion = (fecha) => {
    if (!fecha) {
      return 'La fecha de finalización es obligatoria';
    }
    
    const fechaIngresada = new Date(fecha);
    const hoy = new Date();
    
    if (fechaIngresada > hoy) {
      return 'La fecha de finalización no puede ser futura';
    }
    
    return '';
  };

  const validarDescripcionCierre = (descripcion) => {
    if (!descripcion || descripcion.trim() === '') {
      return 'La descripción del cierre es obligatoria';
    }
    
    if (descripcion.trim().length < 10) {
      return 'La descripción debe tener al menos 10 caracteres';
    }
    
    if (descripcion.trim().length > 500) {
      return 'La descripción no puede exceder los 500 caracteres';
    }
    
    return '';
  };

  const validarValorDefinitivo = (valor, resultadoCierre) => {
    if (!resultadoCierre) {
      return ''; // No validar si no hay resultado de cierre seleccionado
    }
    
    const resultado = parseInt(resultadoCierre);
    
    // Si el resultado es favorable (1) o parcialmente favorable (2), el valor es obligatorio
    if ((resultado === 1 || resultado === 2) && (!valor || valor === '')) {
      return 'El valor definitivo es obligatorio para resultados favorables o parcialmente favorables';
    }
    
    if (valor && valor !== '') {
      const valorNumerico = parseFloat(valor);
      
      if (isNaN(valorNumerico)) {
        return 'El valor definitivo debe ser un número válido';
      }
      
      if (valorNumerico < 0) {
        return 'El valor definitivo no puede ser negativo';
      }
      
      if (valorNumerico > 1000000000) { // Límite de 1,000 millones
        return 'El valor definitivo no puede exceder los 1,000,000,000';
      }
      
      // Validar formato decimal
      if (!/^\d+(\.\d{1,2})?$/.test(valor)) {
        return 'El valor debe tener máximo 2 decimales';
      }
    }
    
    return '';
  };

  const validarCuentaContable = (cuenta, resultadoCierre, valorDefinitivo) => {
    if (!resultadoCierre) {
      return ''; // No validar si no hay resultado de cierre
    }
    
    const resultado = parseInt(resultadoCierre);
    const valor = parseFloat(valorDefinitivo) || 0;
    
    // La cuenta contable es obligatoria solo si hay un valor definitivo positivo
    if ((resultado === 1 || resultado === 2) && valor > 0) {
      if (!cuenta || cuenta.trim() === '') {
        return 'La cuenta contable es obligatoria cuando hay un valor definitivo';
      }
      
      if (!/^[1-9][0-9]*$/.test(cuenta.trim())) {
        return 'La cuenta contable debe contener solo números';
      }
      
      if (cuenta.trim().length < 4) {
        return 'La cuenta contable debe tener al menos 4 dígitos';
      }
      
      if (cuenta.trim().length > 20) {
        return 'La cuenta contable no puede exceder los 20 dígitos';
      }
    }
    
    return '';
  };

  const validarMontoGastosLegales = (monto) => {
  // El campo es obligatorio según el backend
  if (!monto || monto === '' || monto === '0.00') {
    return 'El monto de gastos legales es obligatorio';
  }
  
  const montoNumerico = parseFloat(monto);
  
  if (isNaN(montoNumerico)) {
    return 'El monto de gastos legales debe ser un número válido';
  }
  
  if (montoNumerico < 0) {
    return 'El monto de gastos legales no puede ser negativo';
  }
  
  if (montoNumerico > 10000000) {
    return 'El monto de gastos legales no puede exceder los 10,000,000';
  }
  
  // Validar formato decimal
  if (!/^\d+(\.\d{1,2})?$/.test(monto)) {
    return 'El monto debe tener máximo 2 decimales';
  }
  
  return '';
};


const mostrarErroresValidacion = (errores) => {
  console.log('🔍 Estructura de errores recibida:', errores);
  
  if (typeof errores === 'string') {
    return errores;
  }
  
  if (typeof errores === 'object') {
    const mensajes = [];
    
    // Mapear nombres de campos a nombres legibles en español
    const nombresCampos = {
      'fechafinalizacion': 'Fecha de finalización',
      'resultadocierre': 'Resultado del cierre',
      'descripcioncierre': 'Descripción del cierre',
      'valordefinitivoconciliado': 'Valor definitivo conciliado',
      'cuentavalordefinitivo': 'Cuenta contable',
      'montogastoslegales': 'Monto de gastos legales',
      'errors': 'Errores generales'
    };
    
    // Caso 1: Si viene en propiedad 'errors' (como en tu ejemplo)
    if (errores.errors) {
      if (typeof errores.errors === 'string') {
        return errores.errors;
      } else if (Array.isArray(errores.errors)) {
        return errores.errors.join('; ');
      } else if (typeof errores.errors === 'object') {
        Object.entries(errores.errors).forEach(([campo, mensaje]) => {
          const nombreCampo = nombresCampos[campo] || campo;
          if (Array.isArray(mensaje)) {
            mensajes.push(`${nombreCampo}: ${mensaje.join(', ')}`);
          } else {
            mensajes.push(`${nombreCampo}: ${mensaje}`);
          }
        });
      }
    }
    
    // Caso 2: Si viene directamente en el objeto
    Object.entries(errores).forEach(([campo, mensaje]) => {
      if (campo !== 'errors' && mensaje) {
        const nombreCampo = nombresCampos[campo] || campo;
        
        if (Array.isArray(mensaje)) {
          mensajes.push(`${nombreCampo}: ${mensaje.join(', ')}`);
        } else if (typeof mensaje === 'string') {
          // Limpiar mensajes del backend para hacerlos más amigables
          const mensajeLimpio = mensaje
            .replace(/El campo '.*?' es obligatorio\.?/g, 'es obligatorio')
            .replace(/This field is required\.?/g, 'es obligatorio')
            .replace(/A valid number is required\.?/g, 'debe ser un número válido')
            .replace(/Ensure this value is .*?\.?/g, 'valor no válido');
          
          mensajes.push(`${nombreCampo} ${mensajeLimpio}`);
        } else {
          mensajes.push(`${nombreCampo}: ${mensaje}`);
        }
      }
    });
    
    return mensajes.length > 0 ? mensajes.join('; ') : 'Error de validación en el formulario';
  }
  
  return 'Error de validación en el formulario';
};

  const validarFormulario = () => {
    const nuevosErrores = {};
    
    // Validar cada campo con mensajes específicos
    nuevosErrores.fechafinalizacion = validarFechaFinalizacion(formData.fechafinalizacion);
    nuevosErrores.resultadocierre = !formData.resultadocierre ? 'Seleccione el resultado del cierre' : '';
    nuevosErrores.descripcioncierre = validarDescripcionCierre(formData.descripcioncierre);
    nuevosErrores.valordefinitivoconciliado = validarValorDefinitivo(
      formData.valordefinitivoconciliado, 
      formData.resultadocierre
    );
    nuevosErrores.cuentavalordefinitivo = validarCuentaContable(
      formData.cuentavalordefinitivo,
      formData.resultadocierre,
      formData.valordefinitivoconciliado
    );
    nuevosErrores.montogastoslegales = validarMontoGastosLegales(formData.montogastoslegales);

    setErroresCampos(nuevosErrores);

    // Verificar si hay algún error
    const hayErrores = Object.values(nuevosErrores).some(error => error !== '');
    
    if (hayErrores) {
      // Enfocar el primer campo con error
      const primerCampoConError = Object.keys(nuevosErrores).find(key => nuevosErrores[key] !== '');
      if (primerCampoConError) {
        setTimeout(() => {
          const elementoError = document.querySelector(`[name="${primerCampoConError}"]`);
          if (elementoError) {
            elementoError.focus();
          }
        }, 100);
      }
    }
    
    return hayErrores;
  };

  const manejarEnvio = async () => {
    console.log('🚀 Iniciando proceso de cierre...');
    
    // Validar estado del caso
    if (estadoActual.toLowerCase() === 'cerrado') {
      setError(' Este caso ya se encuentra cerrado y no puede ser modificado');
      return;
    }

    // Validar formulario
    if (validarFormulario()) {
      setError('⚠️ Por favor complete todos los campos requeridos y corrija los errores marcados antes de enviar');
      return;
    }

    try {
      setCargando(true);
      setError(null);
      const encabezados = obtenerEncabezados();

      // Preparar datos EXACTAMENTE como los espera la API
      const datosCierre = {
        fechafinalizacion: convertirFechaAAAAMMDD(formData.fechafinalizacion),
        resultadocierre: parseInt(formData.resultadocierre),
        descripcioncierre: formData.descripcioncierre.trim(),
        valordefinitivoconciliado: parseFloat(formData.valordefinitivoconciliado) ,
        cuentavalordefinitivo: formData.cuentavalordefinitivo || "",
        montogastoslegales: parseFloat(formData.montogastoslegales) 
      };

      console.log('Datos a enviar:', datosCierre);
      console.log('URL:', `https://bei360.injupemp.gob.hn:8444/api/tramite/cerrar/${demandaId}/`);

      // Llamar a la API para cerrar el trámite
      const respuestaCierre = await fetch(
        `https://bei360.injupemp.gob.hn:8444/api/tramite/cerrar/${demandaId}/`,
        {
          method: 'POST',
          headers: encabezados,
          body: JSON.stringify(datosCierre)
        }
      );

      console.log('📨 Respuesta HTTP:', respuestaCierre.status, respuestaCierre.statusText);

      if (!respuestaCierre.ok) {
        let mensajeError = `Error ${respuestaCierre.status}: ${respuestaCierre.statusText}`;
        
        try {
          const errorData = await respuestaCierre.json();
          console.error('📝 Respuesta de error completa:', errorData);
          
          if (errorData.detail) {
            mensajeError = `Error del servidor: ${errorData.detail}`;
          } else if (errorData.message) {
            mensajeError = `Error: ${errorData.message}`;
          } else {
            // Usar la función mejorada de mostrar errores
            const erroresFormateados = mostrarErroresValidacion(errorData);
            mensajeError = `Por favor corrija los siguientes errores:\n${erroresFormateados}`;
          }
        } catch {
          const errorText = await respuestaCierre.text();
          if (errorText) {
            mensajeError = `Error: ${errorText}`;
          }
        }
        
        throw new Error(mensajeError);
      }

      const resultadoCierre = await respuestaCierre.json();
      console.log('✅ Cierre exitoso:', resultadoCierre);

      // Crear un avance especial de cierre
      try {
        const avanceCierreResponse = await fetch(
          'hhttps://bei360.injupemp.gob.hn:8444/api/tramite-avance/crear/',
          {
            method: 'POST',
            headers: encabezados,
            body: JSON.stringify({
              tramiteid: demandaId,
              descripcionavance: `CIERRE DEL CASO - ${formData.descripcioncierre}`,
              usuarioid: sessi.sessionData.id,
              es_cierre: true
            })
          }
        );

        if (avanceCierreResponse.ok) {
          const avanceCierreData = await avanceCierreResponse.json();
          setAvances(prevAvances => [avanceCierreData, ...prevAvances]);
          console.log('📝 Avance de cierre creado exitosamente');
        } else {
          console.warn('⚠️ No se pudo crear el avance de cierre, pero el caso se cerró correctamente');
        }
      } catch (avanceError) {
        console.warn('⚠️ Error al crear avance de cierre:', avanceError);
      }

      // Actualizar estado local
      setEstadoActual('cerrado');
      
      // Obtener datos actualizados de la demanda
      try {
        const respuestaActualizacion = await fetch(
          `https://bei360.injupemp.gob.hn:8444/api/tramite/listar/${demandaId}/`,
          { headers: encabezados }
        );
        
        if (respuestaActualizacion.ok) {
          const datosActualizados = await respuestaActualizacion.json();
          // Si la API devuelve array, tomar primer elemento
          setDemanda(Array.isArray(datosActualizados) ? datosActualizados[0] : datosActualizados);
          console.log('🔄 Datos actualizados obtenidos correctamente');
        }
      } catch (actualizacionError) {
        console.warn('⚠️ No se pudieron obtener los datos actualizados:', actualizacionError);
      }

      // Disparar éxito y cerrar
      onSuccess();
      resetearFormulario();
      onClose();

    } catch (err) {
      console.error('❌ Error completo al cerrar caso:', err);
      setError(`❌ Error al cerrar el caso: ${err.message}`);
      
      // Scroll al top para mostrar el error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setCargando(false);
    }
  };

  // Obtener fecha actual en formato YYYY-MM-DD para el input date
  const obtenerFechaActual = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  // Determinar si la cuenta contable es requerida
  const esCuentaContableRequerida = () => {
    const resultado = parseInt(formData.resultadocierre);
    const valorNumerico = parseFloat(formData.valordefinitivoconciliado) || 0;
    return (resultado === 1 || resultado === 2) && valorNumerico > 0;
  };

  return (
    <Dialog 
      open={open} 
      onClose={manejarCierre}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        fontWeight: 'bold',
        backgroundColor: 'primary.main',
        color: 'white',
        py: 2
      }}>
        <Box display="flex" alignItems="center" justifyContent="center">
          Cerrar Caso #{demandaId || 'N/A'}
          {cargandoDatos && <CircularProgress size={20} sx={{ ml: 2, color: 'white' }} />}
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="body1" gutterBottom sx={{ textAlign: 'center', mb: 3, fontWeight: 'medium' }}>
          Complete toda la información requerida para cerrar el caso
        </Typography>
        
        <Grid container spacing={3}>
          {/* Fecha de Finalización */}
          <Grid item xs={12} sm={6}>
            <TextField
              name="fechafinalizacion"
              label="Fecha de Finalización *"
              type="date"
              fullWidth
              variant="outlined"
              value={formData.fechafinalizacion}
              onChange={manejarCambio('fechafinalizacion')}
              error={!!erroresCampos.fechafinalizacion}
              helperText={erroresCampos.fechafinalizacion || "No puede ser mayor a la fecha actual"}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 
                max: obtenerFechaActual(),
              }}
              required
            />
          </Grid>

          {/* Resultado del Cierre */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!erroresCampos.resultadocierre}>
              <InputLabel>Resultado del Cierre *</InputLabel>
              <Select
                name="resultadocierre"
                value={formData.resultadocierre}
                label="Resultado del Cierre *"
                onChange={manejarCambio('resultadocierre')}
              >
                {opcionesResultado.map((opcion) => (
                  <MenuItem key={opcion.value} value={opcion.value}>
                    {opcion.label}
                  </MenuItem>
                ))}
              </Select>
              {erroresCampos.resultadocierre && (
                <FormHelperText>{erroresCampos.resultadocierre}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Descripción del Cierre */}
          <Grid item xs={12}>
            <TextField
              name="descripcioncierre"
              label="Descripción del Cierre *"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              value={formData.descripcioncierre}
              onChange={manejarCambio('descripcioncierre')}
              error={!!erroresCampos.descripcioncierre}
              helperText={
                erroresCampos.descripcioncierre || 
                `Mínimo 10 caracteres | Máximo 500 caracteres | ${formData.descripcioncierre.length}/500`
              }
              inputProps={{ maxLength: 500 }}
              required
            />
          </Grid>

          {/* Valor Definitivo Conciliado */}
          <Grid item xs={12} sm={6}>
            <TextField
              name="valordefinitivoconciliado"
              label="Valor Definitivo o Conciliado"
              fullWidth
              variant="outlined"
              value={formData.valordefinitivoconciliado}
              onChange={manejarCambio('valordefinitivoconciliado')}
              error={!!erroresCampos.valordefinitivoconciliado}
              helperText={
                erroresCampos.valordefinitivoconciliado || 
                "≥ 0.00 | Máximo 1,000,000,000 | 2 decimales | Obligatorio para resultados favorables"
              }
            />
          </Grid>

          {/* Monto Gastos Legales */}
            <Grid item xs={12} sm={6}>
              <TextField
                name="montogastoslegales"
                label="Monto Gastos Legales u Honorarios *"
                fullWidth
                variant="outlined"
                value={formData.montogastoslegales}
                onChange={manejarCambio('montogastoslegales')}
                error={!!erroresCampos.montogastoslegales}
                helperText={
                  erroresCampos.montogastoslegales || 
                  "Campo obligatorio | ≥ 0.00 | Máximo 10,000,000 | 2 decimales"
                }
                required
              />
            </Grid>

          {/* Cuenta Valor Definitivo */}
          <Grid item xs={12}>
            <TextField
              name="cuentavalordefinitivo"
              label={`Cuenta Contable - Valor Definitivo ${esCuentaContableRequerida() ? '*' : ''}`}
              fullWidth
              variant="outlined"
              value={formData.cuentavalordefinitivo}
              onChange={manejarCambio('cuentavalordefinitivo')}
              error={!!erroresCampos.cuentavalordefinitivo}
              helperText={
                erroresCampos.cuentavalordefinitivo || 
                `${esCuentaContableRequerida() ? 'OBLIGATORIO - ' : ''}Solo números | 4-20 dígitos | ${esCuentaContableRequerida() ? 'Requerido para resultados favorables con valor > 0' : 'Opcional'}`
              }
              inputProps={{ 
                maxLength: 20,
                pattern: '[0-9]*'
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', padding: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <Button 
          onClick={manejarCierre}
          color="error"
          sx={{ minWidth: 120 }}
          disabled={cargando}
        >
          Cancelar
        </Button>
        
        <Button 
          onClick={manejarEnvio}
          variant="contained" 
          color="primary"
          disabled={cargando}
          startIcon={cargando ? <CircularProgress size={20} /> : null}
          sx={{ minWidth: 150 }}
        >
          {cargando ? 'Cerrando...' : 'Confirmar Cierre'}
        </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CierreCaso;